import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { usePageTitle } from 'contexts/PageTitleContext';
import { AppOrderDetailsView } from './productList';

const AppOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPaymentOrderId, setCheckingPaymentOrderId] = useState(null);

  useEffect(() => {
    setPageTitle('App Order');
  }, [setPageTitle]);

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setOrder(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      const decodedOrderId = decodeURIComponent(orderId);
      const url = `${API_ENDPOINTS.GET_products}?user_id=${userId}&type=normal&search=${encodeURIComponent(decodedOrderId)}&page_size=50`;
      const response = await apiService.get(url);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];
      const found =
        list.find(
          (item) =>
            String(item.id) === String(decodedOrderId) ||
            String(item.order_id) === String(decodedOrderId)
        ) || null;
      setOrder(found);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleCheckPaymentStatus = async (orderData) => {
    const targetOrderId = orderData?.order_id ?? orderData?.id;
    if (!targetOrderId) return;
    const userId = localStorage.getItem('user_id') || '';
    setCheckingPaymentOrderId(targetOrderId);
    try {
      const url = `${API_ENDPOINTS.Refund_button_click}?user_id=${encodeURIComponent(userId)}&order_id=${encodeURIComponent(targetOrderId)}`;
      const response = await apiService.get(url);
      if (response?.success) {
        await fetchOrder();
      } else if (!response?.success) {
        alert(response?.message || 'Check payment status failed.');
      }
    } catch (error) {
      console.error('Check payment status error:', error);
      alert(error?.message || 'Check payment status failed.');
    } finally {
      setCheckingPaymentOrderId(null);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard/orders-management')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
      >
        Back to Orders
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : order ? (
        <Card sx={{ overflow: 'visible' }}>
          <AppOrderDetailsView
            order={order}
            onCheckPaymentStatus={handleCheckPaymentStatus}
            checkingPaymentOrderId={checkingPaymentOrderId}
          />
        </Card>
      ) : (
        <Typography color="text.secondary">Order not found.</Typography>
      )}
    </Box>
  );
};

export default AppOrderDetails;
